import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { Clock, Sparkles, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { API, AuthContext } from '../App';

/**
 * Phase 26 — Trial countdown banner.
 * Shown only to users who are still in the 7-day trial AND have not been
 * SuperAdmin-approved AND whose company has not paid.
 *
 * Calls /api/trial/status (whitelisted even when trial expires so the user
 * sees a helpful CTA instead of a wall).
 */
export default function TrialBanner() {
  const { user } = useContext(AuthContext);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!user || user.role === 'SUPERADMIN' || user.is_demo) return;
    let cancelled = false;
    axios.get(`${API}/trial/status`, { withCredentials: true })
      .then(r => { if (!cancelled) setStatus(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  if (!status || !status.should_show_banner) return null;

  if (status.expired) {
    return (
      <div data-testid="trial-banner-expired" className="bg-gradient-to-r from-rose-600 to-red-700 text-white px-4 py-2.5 shadow-md">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 shrink-0" />
            <div>
              <strong>Your 7-day trial has ended.</strong>
              <span className="ml-2 opacity-90">Subscribe to continue using FM Flexi Pro, or wait for SuperAdmin approval.</span>
            </div>
          </div>
          <Link
            to="/buy-now"
            className="bg-white text-rose-700 hover:bg-rose-50 px-4 py-1.5 rounded-full text-xs font-semibold transition"
          >
            Subscribe now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="trial-banner-active" className="bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 px-4 py-2 shadow-sm">
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <strong>
            Free Trial — <Clock className="w-3.5 h-3.5 inline-block -mt-0.5" /> {status.days_remaining} day{status.days_remaining === 1 ? '' : 's'} remaining
          </strong>
          <span className="opacity-80 hidden md:inline">· Full access while you evaluate. Subscribe before it ends to keep your data.</span>
        </div>
        <Link
          to="/buy-now"
          className="bg-slate-900 text-amber-300 hover:bg-slate-800 px-4 py-1.5 rounded-full text-xs font-semibold transition"
        >
          Upgrade
        </Link>
      </div>
    </div>
  );
}
